import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { aiAssistantService } from "@/services/aiAssistantService";
import { useAuth } from "@/contexts/AuthContext";
import { GradientPageHeader } from "@/components/layout/GradientPageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SmartIcon } from "@/components/ui/SmartIcon";
import { PublicAIAssistantsSection } from "@/components/agents-list/PublicAIAssistantsSection";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputToolbar,
  PromptInputModelSelect,
  PromptInputModelSelectTrigger,
  PromptInputModelSelectContent,
  PromptInputModelSelectItem,
  PromptInputModelSelectValue,
} from "@/components/ai-elements/prompt-input";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Bot, Search, Wand2, Plus } from "lucide-react";

export default function PublicLanding() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedChatAssistantId, setSelectedChatAssistantId] = useState<
    string | null
  >(null);
  const [masterPrompt, setMasterPrompt] = useState<string>("");

  const rotatingSuggestions = React.useMemo(
    () => [
      "Plan a project",
      "Reply to an email",
      "Summarize a meeting",
      "Write a LinkedIn post",
      "Prep for a meeting",
      "Write a blog article",
    ],
    [],
  );
  const [suggestionIndex, setSuggestionIndex] = useState(0);

  React.useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const onSelect = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    onSelect();
    carouselApi.on("reInit", onSelect);
    carouselApi.on("select", onSelect);

    return () => {
      carouselApi?.off("select", onSelect);
    };
  }, [carouselApi]);

  React.useEffect(() => {
    const id = setInterval(() => {
      setSuggestionIndex((i) => (i + 1) % rotatingSuggestions.length);
    }, 3000);
    return () => clearInterval(id);
  }, [rotatingSuggestions.length]);

  useEffect(() => {
    if (isLoading) return;
    if (user) {
      navigate("/ai-assistants", { replace: true });
    }
  }, [user, isLoading, navigate]);

  const { data: systemAssistantsResponse, isLoading: isLoadingAIAssistants } =
    useQuery({
      queryKey: ["public-system-ai-assistants"],
      queryFn: () => aiAssistantService.getSystemAssistants(),
    });

  // Extract and scope assistants: show only public/global assistants for public preview
  const systemAssistants = systemAssistantsResponse?.data || [];
  const availableAssistants = useMemo(() => {
    // For public landing, only show global assistants (not organization-specific)
    return systemAssistants.filter(
      (a: any) =>
        a.is_active === true &&
        (a.owner_organization_id === null ||
          a.owner_organization_id === undefined),
    );
  }, [systemAssistants]);

  // Get unique categories from assistants
  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(availableAssistants.map((a) => a.category)),
    );
    const desiredOrder = [
      "Marketing",
      "Sales",
      "Daily work",
      "Customer Engagement",
      "Operations",
      "Finance",
      "HR",
      "Legal",
    ];
    const ordered = desiredOrder.filter((c) => unique.includes(c));
    const remaining = unique.filter((c) => !desiredOrder.includes(c)).sort();
    return ["All", ...ordered, ...remaining];
  }, [availableAssistants]);

  // Separate chat assistants from generation assistants
  const chatAssistants = useMemo(() => {
    return availableAssistants.filter((a: any) => a.supports_chat === true);
  }, [availableAssistants]);

  const generationAssistants = useMemo(() => {
    return availableAssistants.filter((a: any) => a.supports_chat !== true);
  }, [availableAssistants]);

  // Filter assistants based on selected category and search query
  const filteredAssistants = useMemo(() => {
    let filtered = generationAssistants;

    // Filter by category
    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        (assistant) => assistant.category === selectedCategory,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (assistant) =>
          assistant.title.toLowerCase().includes(query) ||
          assistant.description.toLowerCase().includes(query) ||
          assistant.name.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [generationAssistants, selectedCategory, searchQuery]);

  // Filter chat assistants for search and sort A→Z by description text
  const filteredChatAssistants = useMemo(() => {
    const list = !searchQuery.trim()
      ? chatAssistants
      : chatAssistants.filter((assistant: any) => {
          const query = searchQuery.toLowerCase().trim();
          return (
            assistant.title?.toLowerCase().includes(query) ||
            assistant.description?.toLowerCase().includes(query) ||
            assistant.name?.toLowerCase().includes(query)
          );
        });

    return [...list].sort((a: any, b: any) => {
      const da = (a.description || "").toString().trim().toLowerCase();
      const db = (b.description || "").toString().trim().toLowerCase();
      if (da < db) return -1;
      if (da > db) return 1;
      return 0;
    });
  }, [chatAssistants, searchQuery]);

  const handlePromptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/register");
  };

  const handleOpenChat = () => {
    navigate("/register");
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col min-h-full">
        {/* Logo Section with dark background like sidebar */}
        <div
          className="pt-4 pb-4"
          style={{ backgroundColor: "hsl(var(--primary))" }}
        >
          <div className="flex justify-center">
            <img
              src="https://gvaimfxjrywshlnkxrbi.supabase.co/storage/v1/object/public/image_library/public_images/logo%20horizontal%20white%20agents.svg"
              alt="FirmAgent Logo"
              className="h-8 w-auto max-w-[200px] object-contain"
            />
          </div>
        </div>

        <GradientPageHeader
          title="What do you need help with today?"
          titleHighlight="today"
          description="Ask something or select an assistant or tool to get started"
          belowDescription={
            <div className="max-w-[44rem] mx-auto relative">
              <style
                dangerouslySetInnerHTML={{
                  __html: `@keyframes borderGlow { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }`,
                }}
              />
              {/* Animated gradient border wrapper */}
              <div
                className="relative rounded-xl"
                style={{
                  border: "2px solid transparent",
                  // white fill as padding-box, animated gradient as border-box
                  background:
                    "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, rgb(158, 172, 211), rgb(234, 179, 8), rgba(255,255,255,0.95), rgb(158, 172, 211), rgba(255,255,255,0.95)) border-box",
                  backgroundSize: "400% 100%",
                  animation: "borderGlow 8s linear infinite",
                }}
              >
                <PromptInput
                  onSubmit={handlePromptSubmit}
                  className="relative rounded-xl bg-white"
                >
                  <PromptInputTextarea
                    value={masterPrompt}
                    onChange={(e) => setMasterPrompt(e.target.value)}
                    placeholder={rotatingSuggestions[suggestionIndex]}
                    className="min-h-[56px] border-none"
                    autoFocus
                  />

                  <PromptInputToolbar>
                    {/* Inline assistant selector dropdown using shadcn (styled like model select) */}
                    <div className="flex items-center gap-2">
                      <PromptInputModelSelect
                        value={selectedChatAssistantId || undefined}
                        onValueChange={(v) => setSelectedChatAssistantId(v)}
                      >
                        <PromptInputModelSelectTrigger>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Wand2 className="h-4 w-4" />
                            <PromptInputModelSelectValue placeholder="Select an assistant" />
                          </div>
                        </PromptInputModelSelectTrigger>
                        <PromptInputModelSelectContent className="min-w-[280px]">
                          {chatAssistants.map((a: any) => (
                            <PromptInputModelSelectItem key={a.id} value={a.id}>
                              <span className="flex items-center gap-2">
                                <SmartIcon
                                  iconUrl={a.icon_url}
                                  size={18}
                                  className="shrink-0 rounded-sm"
                                  alt={`${a.title || a.name} icon`}
                                />
                                <span>
                                  {(a.name || "Untitled").trim()}{" "}
                                  {a.title ? `- ${a.title}` : ""}
                                </span>
                              </span>
                            </PromptInputModelSelectItem>
                          ))}
                        </PromptInputModelSelectContent>
                      </PromptInputModelSelect>
                    </div>

                    <div className="flex items-center gap-1">
                      <PromptInputSubmit />
                    </div>
                  </PromptInputToolbar>
                </PromptInput>
              </div>
            </div>
          }
        />

        {/* Page content now scrolls normally; header is not blocking */}

        <div className="flex-1 -mt-4">
          <div className="container mx-auto px-6 py-2 w-full max-w-[68rem]">
            {/* AI Assistants Section */}
            {filteredChatAssistants.length > 0 && (
              <div className="mb-3">
                <Carousel
                  setApi={setCarouselApi}
                  opts={{
                    align: "center",
                    loop: false,
                  }}
                  className="w-full"
                >
                  <CarouselContent className="justify-center">
                    {filteredChatAssistants.map((assistant: any) => (
                      <CarouselItem
                        key={assistant.id}
                        className="pl-2 md:pl-4 basis-auto"
                      >
                        <button
                          type="button"
                          onClick={handleOpenChat}
                          className="flex flex-col items-center gap-3 group w-32 p-2"
                          title="Sign up to use this assistant"
                        >
                          <div className="relative w-16 h-16 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm transition-all duration-500 ease-out group-hover:shadow-2xl group-hover:shadow-blue-500/25 group-hover:scale-110 group-hover:border-transparent overflow-visible">
                            {/* Futuristic ring effects - only on hover */}
                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-cyan-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 group-hover:animate-pulse"></div>
                            <div className="absolute inset-0 rounded-full border-2 border-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-border opacity-0 group-hover:opacity-60 transition-opacity duration-500"></div>
                            <div className="absolute inset-1 rounded-full bg-white group-hover:bg-gradient-to-br group-hover:from-blue-50 group-hover:to-indigo-100 transition-all duration-500"></div>

                            {/* Icon container */}
                            <div className="relative z-10 transition-all duration-500 group-hover:drop-shadow-lg">
                              <SmartIcon
                                iconUrl={assistant.icon_url}
                                size={56}
                                fallbackIcon={<Bot className="h-6 w-6" />}
                                alt={`${assistant.title || "AI tool"} icon`}
                                className="opacity-80"
                              />
                            </div>

                            {/* Animated particles - only on hover */}
                            <div className="absolute inset-0 rounded-full overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                              <div className="absolute top-2 left-2 w-1 h-1 bg-blue-400 rounded-full animate-ping"></div>
                              <div
                                className="absolute bottom-3 right-4 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping"
                                style={{ animationDelay: "0.5s" }}
                              ></div>
                              <div
                                className="absolute top-4 right-2 w-1 h-1 bg-cyan-400 rounded-full animate-ping"
                                style={{ animationDelay: "1s" }}
                              ></div>
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-sm font-semibold text-slate-800 leading-tight group-hover:text-blue-700 transition-colors duration-300">
                              {assistant.title}
                            </div>
                            <div className="text-xs text-gray-500">
                              {assistant.name}
                            </div>
                          </div>
                        </button>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {canScrollPrev && <CarouselPrevious className="left-0" />}
                  {canScrollNext && <CarouselNext className="right-0" />}
                </Carousel>
              </div>
            )}

            {/* AI Tools Section Header */}
            <div className="mb-6 mt-16">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: "rgba(158, 172, 211, 0.1)" }}
                  >
                    <Bot
                      className="h-6 w-6"
                      style={{ color: "rgb(158, 172, 211)" }}
                    />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800">AI Tools</h2>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/register")}
                  className="flex items-center gap-2 hover:bg-gray-50 text-gray-600"
                >
                  <Plus className="h-4 w-4" />
                  Create AI tool
                </Button>
              </div>

              {/* Search and Filters */}
              {!isLoadingAIAssistants && systemAssistants.length > 0 && (
                <div className="space-y-4">
                  {/* Search Bar with animated gradient border like main input */}
                  <div
                    className="relative w-2/3 max-w-xs rounded-xl"
                    style={{
                      border: "2px solid transparent",
                      background:
                        "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, rgb(158, 172, 211), rgb(234, 179, 8), rgba(255,255,255,0.95), rgb(158, 172, 211), rgba(255,255,255,0.95)) border-box",
                      backgroundSize: "400% 100%",
                      animation: "borderGlow 8s linear infinite",
                    }}
                  >
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search AI tools..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-white border-none rounded-xl"
                      style={
                        {
                          "--tw-ring-color": "rgb(158, 172, 211)",
                        } as React.CSSProperties
                      }
                    />
                  </div>

                  {/* Category Filters */}
                  {categories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {categories.map((category) => (
                        <Button
                          key={category}
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedCategory(category)}
                          className={
                            selectedCategory === category
                              ? "border-2 border-gray-400 bg-gray-50 text-gray-700 font-medium"
                              : "border border-gray-200 text-gray-600 hover:border-gray-300"
                          }
                        >
                          {category}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Split into sections: only public/global assistants */}
            {(() => {
              // Since we already filtered to only global assistants, all are public assistants
              // Hubflo general assistants: industry_id is null
              const hubfloGeneral = filteredAssistants.filter(
                (a: any) => !a.industry_id,
              );
              // Group other industry assistants by industry
              const otherIndustryAssistants = filteredAssistants.filter(
                (a: any) => a.industry_id,
              );

              // Group by industry for better organization
              const groupedByIndustry = otherIndustryAssistants.reduce(
                (acc: any, assistant: any) => {
                  const industry = assistant.industries;
                  if (industry) {
                    const key = industry.id;
                    if (!acc[key]) {
                      acc[key] = {
                        industry: industry,
                        assistants: [],
                      };
                    }
                    acc[key].assistants.push(assistant);
                  }
                  return acc;
                },
                {},
              );

              return (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      General AI tools
                    </h3>
                    <PublicAIAssistantsSection
                      assistants={hubfloGeneral}
                      isLoading={isLoadingAIAssistants}
                      searchQuery={searchQuery}
                      selectedCategory={selectedCategory}
                      hideIndustryTags={true}
                    />
                  </div>
                  {/* Grouped industry sections */}
                  {Object.values(groupedByIndustry).map((group: any) => (
                    <div key={group.industry.id}>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        {group.industry.emoji && (
                          <span>{group.industry.emoji}</span>
                        )}
                        {group.industry.name} assistants
                      </h3>
                      <PublicAIAssistantsSection
                        assistants={group.assistants}
                        isLoading={isLoadingAIAssistants}
                        searchQuery={searchQuery}
                        selectedCategory={selectedCategory}
                        hideIndustryTags={true}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
